/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For mor information visit: https://egtuniverse.com/legal
 * 
 * Created for Universe App Tools.
 * 
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.code",
  title: "Code Editor",
  description: "A code editor for javascript, json, html, and css.",
  control: true,
  preview: "ua.preview.text",
  supportServer: false,
  fields: {
    "name": {
      title: "Name",
      type: "string",
      description: "The name of the control.",
      required: true
    },
    "type": {
      title: "Type",
      type: "string",
      list: {
        must: ["javascript", "json", "html", "css"]
      },
      description: "The type of code to render.",
      required: true
    },
    "inner": {
      title: "Inner",
      type: "string",
      description: "The inner code to render."
    }
  },
  passthrough: {
    except: ["inner", "type", "id"]
  }
});

/*

<!doctype html>
<head>
    <meta charset="utf8">
    <style type="text/css">
    body {
        padding:0px;
        margin:0px;
        background: #171b1f;
    }
    doc {
        display:none;
    }
   </style>
</head>
<!-- <doc type="javascript">
function test() {
    return false;
}
</doc> -->
<doc type="json">
[
{
    "this": false;
    "this": false
}
]

</doc>
<script src="editor.bundle.js"></script>
*/

var f = require("../scripts/f.js");
var REQUEST_NAMESPACE = "ua.code";
var REQUEST_TIMEOUT_MS = 2500;
var CODE_BUNDLE_URI = "build/code.editor.bundle.js";

function safeString(value) {
  if (typeof value === "string") return value;
  if (typeof value === "undefined" || value === null) return "";
  return String(value);
}

function getIframe(control) {
  if (!control) return null;
  return control.querySelector("iframe");
}

function getControlType(control, fallbackType) {
  var fromAttr = control && (control.getAttribute("type") || control.getAttribute("data-type"));
  if (fromAttr) return safeString(fromAttr).toLowerCase();

  try {
    var iframe = getIframe(control);
    var doc = iframe && iframe.contentDocument;
    var node = doc && doc.querySelector("doc");
    var fromDoc = node && node.getAttribute("type");
    if (fromDoc) return safeString(fromDoc).toLowerCase();
  } catch (error) {}

  return safeString(fallbackType || "json").toLowerCase();
}

async function waitForIframe(iframe) {
  if (!iframe) return false;
  if (iframe.contentWindow && iframe.contentDocument && iframe.contentDocument.readyState === "complete") return true;

  await new Promise(function(resolve) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      resolve();
    }
    iframe.addEventListener("load", finish, { once: true });
    setTimeout(finish, 1500);
  });

  return !!(iframe.contentWindow && iframe.contentDocument);
}

function fallbackReadFromDoc(iframe) {
  try {
    var doc = iframe && iframe.contentDocument;
    if (!doc) return "";
    var node = doc.querySelector("doc");
    if (!node) return "";
    return safeString(node.innerText || node.textContent || "");
  } catch (error) {
    return "";
  }
}

async function sendBridgeRequest(iframe, action, payload) {
  await waitForIframe(iframe);

  var contentWindow = iframe && iframe.contentWindow;
  if (!contentWindow) {
    throw new Error("Code editor frame is not available.");
  }

  if (contentWindow.__uaCodeEditorBridge && typeof contentWindow.__uaCodeEditorBridge[action] === "function") {
    return await contentWindow.__uaCodeEditorBridge[action](payload);
  }

  var requestId = "req_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
  var message = {
    namespace: REQUEST_NAMESPACE,
    requestId: requestId,
    action: action,
    payload: payload
  };

  var response = await new Promise(function(resolve, reject) {
    var timer = setTimeout(function() {
      window.removeEventListener("message", onMessage);
      reject(new Error("Timed out waiting for code editor response."));
    }, REQUEST_TIMEOUT_MS);

    function onMessage(event) {
      var data = event && event.data;
      if (!data || data.namespace !== REQUEST_NAMESPACE || data.requestId !== requestId) return;

      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve(data);
    }

    window.addEventListener("message", onMessage);
    contentWindow.postMessage(message, "*");
  });

  if (response && response.success === false) {
    throw new Error(response.message || "Code editor action failed.");
  }

  return response || {};
}

async function readSource(control) {
  var iframe = getIframe(control);
  if (!iframe) return "";
  var type = getControlType(control, "json");

  try {
    var reply = await sendBridgeRequest(iframe, "getSource", { type: type });
    return safeString(reply.source);
  } catch (error) {
    return fallbackReadFromDoc(iframe);
  }
}

async function writeSource(control, source) {
  var iframe = getIframe(control);
  if (!iframe) return false;
  var type = getControlType(control, "json");

  try {
    await sendBridgeRequest(iframe, "setSource", { source: safeString(source), type: type });
    return true;
  } catch (error) {
    try {
      var doc = iframe.contentDocument;
      if (!doc) return false;
      var node = doc.querySelector("doc");
      if (!node) return false;
      node.innerText = safeString(source);
      node.setAttribute("type", type);
      return true;
    } catch (writeError) {
      return false;
    }
  }
}

async function render(options) {

  var iframe = document.createElement("iframe");
  var type = safeString(options.type || "json");
  var inner = safeString(options.inner);

  var bundleSrc = f.FullUrl() + CODE_BUNDLE_URI;
  var html = `<!doctype html>
  <head>
      <meta charset="utf8">
      <style type="text/css">
      body {
          padding:0px;
          margin:0px;
          background: #171b1f;
      }
     </style>
  </head>
  <body>
  <doc type="${type}">${inner}</doc>
  <script>
  (function() {
    function setupFallbackBridge(initialValue) {
      var body = document.body || document.documentElement;
      var docNode = document.querySelector("doc");
      var editorValue = typeof initialValue === "string" ? initialValue : (docNode ? (docNode.innerText || docNode.textContent || "") : "");

      if (docNode) docNode.style.display = "none";

      var area = document.createElement("textarea");
      area.setAttribute("spellcheck", "false");
      area.style.width = "100%";
      area.style.height = "22rem";
      area.style.background = "#171b1f";
      area.style.color = "#e6edf3";
      area.style.border = "1px solid #30363d";
      area.style.padding = "0.75rem";
      area.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
      area.style.fontSize = "0.875rem";
      area.value = editorValue;
      body.appendChild(area);

      window.__uaCodeEditorBridge = {
        getSource: function() {
          return area.value || "";
        },
        setSource: function(payload) {
          var source = payload && typeof payload === "object" ? payload.source : "";
          area.value = typeof source === "string" ? source : "";
          return true;
        }
      };

      window.addEventListener("message", function(event) {
        var data = event && event.data;
        if (!data || data.namespace !== "ua.code" || !data.requestId || !data.action) return;

        var response = {
          namespace: "ua.code",
          requestId: data.requestId,
          success: true
        };

        try {
          if (data.action === "getSource") {
            response.source = area.value || "";
          } else if (data.action === "setSource") {
            var payload = data.payload || {};
            var source = payload && typeof payload === "object" ? payload.source : "";
            area.value = typeof source === "string" ? source : "";
            response.source = area.value || "";
          } else {
            response.success = false;
            response.message = "Unsupported action.";
          }
        } catch (error) {
          response.success = false;
          response.message = (error && error.message) ? error.message : String(error);
        }

        try {
          window.parent.postMessage(response, "*");
        } catch (error) {}
      }, false);
    }

    window.__uaCodeFallbackInit = setupFallbackBridge;
  }());
  </script>
  <script src="${bundleSrc}" onerror="window.__uaCodeFallbackInit && window.__uaCodeFallbackInit();"></script>
  </body>
  </html>`;

  var blob = new Blob([html], { type: "text/html" });
  iframe.src = URL.createObjectURL(blob);
  iframe.classList.add("card");
  iframe.classList.add("iframedoc");
  iframe.setAttribute("sandbox", "allow-same-origin allow-popups allow-scripts allow-forms");
  return iframe;
} module.exports.render = render;

/**
 * Saves the code from the iframe.
 * Called by UATools.
 * @param {*} name The name of the control.
 * @param {*} control The control to save.
 * @param {*} repo The repo (used to send back), with the prevalidation results.
 * @property {*} repo.success The success object (is this field ready to be saved).
 * @property {*} repo.data The actual data object that will be saved. JSON encodable only (no functions or promises).
 * @property {*} repo.errors The error's applied to the object. Should be an array, can have more than one item.
 * @property {*} repo.errors.input If appliable, the direct input that caused the erorr - it must be an object. If input is not provided the control will be highlighted.
 * @property {*} repo.errors.input.id The id of the input field, if applicable.
 * @property {*} repo.errors.input.name The name of the input field if applicable.
 * @property {*} repo.errors.type The type of error that occured.
 *  - Supports: "validation" - The input or field or control is invalid
 *  - Supports: "thowable" - Processing this field caused a throwable to error out.
 * @property {*} repo.errors.message The message to display to the user.
 * @returns The repo object with the data to save
 */
async function save(name, control, repo) {
  if (!(repo.errors instanceof Array)) repo.errors = [];
  try {
    repo.data = await readSource(control);
  } catch (error) {
    repo.success = false;
    repo.errors.push({
      type: "validation",
      message: "The code could not be saved.",
      error: error
    });
  }

  return repo;

} module.exports.save = save;

async function load(name, control, data) {
  await writeSource(control, safeString(data));
}

module.exports.load = load;
